/// <amd-module name="Reporting/BoardsCommon/Pickers/AggregationPicker" />
import { AggregationFieldsQuery, getDistinctFieldsOfTypes } from "Analytics/Boards/BoardsQueries/AggregationFieldsQuery";
import { ProcessesQuery } from "Analytics/Boards/BoardsQueries/ProcessesQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import * as React from "react";
import * as Resources from "Reporting/BoardsCommon/Resources";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { IListSelection } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { WorkItemBacklogFilter } from "./WorkItemBacklogFilter";

export interface AggregationSettings extends ModefulValueSetting<AggregationMode, string> {}

export interface WorkItemAggregationOption extends IListBoxItem<AggregationSettings> {}

export interface AggregationPickerProps {
    items: WorkItemAggregationOption[];
    /** Uses ReferenceName of  fileld for sums. Not finalized */
    value: AggregationSettings;
    onSelect: (newValue: AggregationSettings) => void;
}

/** A widget/report agnostic picker for aggregation data. */
export class AggregationPicker extends VssComponent<AggregationPickerProps> {
    private selection: IListSelection = new DropdownSelection();

    public componentDidMount(): void {
        this.applySelection(this.props);
    }

    public componentWillReceiveProps(newProps: AggregationPickerProps): void {
        if (JSON.stringify(newProps) != JSON.stringify(this.props)) {
            this.applySelection(newProps);
        }
    }

    public render(): JSX.Element {
        return (
            <Dropdown
                className="aggregation-picker"
                items={this.props.items}
                selection={this.selection}
                onSelect={this.onSelect}
                showFilterBox={this.props.items.length > 7}
            />
        );
    }

    private applySelection(props: AggregationPickerProps): void {
        // Apply Selection from current props
        const selectedItemId = aggregationSettingsToId(props.value);
        const selectionPosition = props.items.findIndex(item => {
            return item.id === selectedItemId;
        });
        if (selectionPosition >= 0) {
            this.selection.select(selectionPosition, 1, false);
        } else {
            this.selection.clear();
        }
    }

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, item: WorkItemAggregationOption): void => {
        this.props.onSelect(item.data!);
    };
}

/** Provides a low-overhead string-comparable Id  */
function aggregationSettingsToId(settings: AggregationSettings): string {
    return settings.identifier === AggregationMode.Sum
        ? AggregationMode[settings.identifier] + "_" + settings.settings
        : AggregationMode[settings.identifier];
}

export async function getAggregationOptions(
    pageContext: IVssPageContext,
    projectId: string,
    teamId: string,
    workItemTypeFilter: WorkItemBacklogFilter,
    isAutomationScenario?: boolean
): Promise<WorkItemAggregationOption[]> {
    let aggregationOptionsPromise;
    if (workItemTypeFilter.identifier === "BacklogCategory") {
        aggregationOptionsPromise = getAggregationOptionsForBacklogCategories(
            pageContext,
            projectId,
            teamId,
            [workItemTypeFilter.settings],
            isAutomationScenario
        );
    } else {
        aggregationOptionsPromise = getAggregationOptionsForWorkItemTypes(
            pageContext,
            projectId,
            [workItemTypeFilter.settings],
            isAutomationScenario
        );
    }
    return aggregationOptionsPromise;
}

/** Ensure settings - We prefer prior settings, then fall back to remaining work, and finally use count if all else fails. */
export function ensureAggregationSettings(aggregationOptions: WorkItemAggregationOption[], priorSettings?: AggregationSettings): AggregationSettings {
    const remainingWorkField = "Microsoft.VSTS.Scheduling.RemainingWork";

    let settings = {
        identifier: AggregationMode.Count,
        settings: ""
    };

    if (
        priorSettings != null &&
        (priorSettings.identifier === AggregationMode.Count ||
            aggregationOptions.some(item => {
                return priorSettings.settings === item.data!.settings;
            }))
    ) {
        settings = priorSettings;
    } else if (
        aggregationOptions.some(item => {
            return remainingWorkField === item.data!.settings;
        })
    ) {
        settings = {
            identifier: AggregationMode.Sum,
            settings: remainingWorkField
        };
    }

    return settings;
}

export async function getAggregationOptionsForBacklogCategories(
    pageContext: IVssPageContext,
    projectId: string,
    teamId: string,
    backlogCategoryReferenceNames: string[],
    isAutomationScenario?: boolean
): Promise<WorkItemAggregationOption[]> {
    const workItemTypes = await getWorkItemTypesOfCategories(pageContext, projectId, teamId, backlogCategoryReferenceNames, isAutomationScenario);
    return getAggregationOptionsForWorkItemTypes(pageContext, projectId, workItemTypes, isAutomationScenario);
}

export async function getAggregationOptionsForWorkItemTypes(
    pageContext: IVssPageContext,
    projectId: string,
    workItemTypes: string[],
    isAutomationScenario?: boolean
): Promise<WorkItemAggregationOption[]> {
    const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
    const query = new AggregationFieldsQuery(projectId).setAutomationMode(isAutomationScenario);

    const headerData = {
        identifier: AggregationMode.Count,
        settings: ""
    };
    const header: WorkItemAggregationOption = {
        id: aggregationSettingsToId(headerData),
        text: Resources.AggregationPicker_CountOfWorkItems,
        data: headerData
    };

    const results = getDistinctFieldsOfTypes(workItemTypes, await queryCache.getCacheableQueryResult(query)).map(item => {
        const settingData = {
            identifier: AggregationMode.Sum,
            settings: item.FieldReferenceName
        };
        return {
            id: aggregationSettingsToId(settingData),
            text: format(Resources.AggregationPicker_SumOfNamedProperty, item.FieldName),
            data: settingData
        } as WorkItemAggregationOption;
    });
    const items = [header].concat(results);
    return items;
}

/**  Get the work item types associated with the supplied backlogCategory.*/
async function getWorkItemTypesOfCategories(
    pageContext: IVssPageContext,
    projectId: string,
    teamId: string,
    backlogCategoryReferenceNames: string[],
    isAutomationScenario?: boolean
): Promise<string[]> {
    // Ax - Get the work item types associated to that backlog level
    const queryCache = pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
    const processes = await queryCache.getCacheableQueryResult(
        ProcessesQuery.onTeam(pageContext, projectId, teamId).setAutomationMode(isAutomationScenario)
    );
    const workItemTypes: string[] = processes
        .filter(o => backlogCategoryReferenceNames.some(name => o.BacklogCategoryReferenceName === name))
        .map(entry => {
            return entry.WorkItemType;
        });

    return workItemTypes;
}
